<template>
  <modal :show="show" @update:show="handleModalClose">
    <div class="box">
      <div slot="header">
        {{ title }}
      </div>

      <div class="content">
        <!-- Warning message when dependencies exist -->
        <div v-if="hasDependencies" class="warning-message">
          <p class="has-text-weight-semibold has-text-warning">
            {{ dependencyWarningMessage }}
          </p>
          
          <!-- DataInput dependencies -->
          <div v-if="dataInputNames.length > 0" class="dependency-section">
            <strong>{{ dataInputLabel }}:</strong>
            <ul class="dependency-list">
              <li v-for="name in dataInputNames" :key="name" class="dependency-item">
                {{ name }}
              </li>
            </ul>
          </div>
          
          <!-- Forwarder dependencies -->
          <div v-if="forwarderNames.length > 0" class="dependency-section">
            <strong>{{ forwarderLabel }}:</strong>
            <ul class="dependency-list">
              <li v-for="name in forwarderNames" :key="name" class="dependency-item">
                {{ name }}
              </li>
            </ul>
          </div>
        </div>
        
        <!-- Normal confirmation when no dependencies -->
        <div v-else>
          <p class="has-text-weight-semibold">
            {{ message }}
          </p>
          <p class="has-text-grey">
            {{ warningText }}
          </p>
        </div>
        
        <!-- Countdown display (only show when countdown is enabled and no dependencies) -->
        <div v-if="countdownActive && !hasDependencies && showCountdown" class="notification is-warning">
          <p class="has-text-weight-semibold">
            {{ countdownMessage }}
          </p>
        </div>
      </div>

      <div slot="footer" class="field is-grouped is-grouped-right">
        <div class="control">
          <button 
            type="button" 
            class="button is-link is-light"
            @click="handleCancel"
          >
            {{ cancelText }}
          </button>
        </div>
        <div class="control">
          <button 
            type="button" 
            class="button"
            :class="confirmButtonClass"
            @click="handleConfirm"
            :disabled="countdownActive || hasDependencies"
            :style="{ visibility: (countdownActive && !hasDependencies && showCountdown) ? 'hidden' : 'visible' }"
          >
            {{ confirmText }}
          </button>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import { Modal } from '@cyradar/ui'

/**
 * ConfirmationDialog Component
 * 
 * A reusable confirmation dialog component for delete and status change actions.
 * Based on SourceTypeDeleteConfirmation as the standard.
 * 
 * Features:
 * - Optional countdown timer to prevent accidental actions
 * - Dependency warning display
 * - Customizable messages and button styles
 */
export default {
  name: 'ConfirmationDialog',
  components: {
    Modal
  },
  props: {
    /**
     * Controls the visibility of the modal
     */
    show: {
      type: Boolean,
      default: false
    },
    /**
     * Dialog title
     */
    title: {
      type: String,
      required: true
    },
    /**
     * Main confirmation message
     */
    message: {
      type: String,
      required: true
    },
    /**
     * Warning text (shown in grey)
     */
    warningText: {
      type: String,
      default: ''
    },
    /**
     * Cancel button text
     */
    cancelText: {
      type: String,
      default: 'Hủy'
    },
    /**
     * Confirm button text
     */
    confirmText: {
      type: String,
      default: 'Xác nhận'
    },
    /**
     * Confirm button class (e.g., 'is-danger', 'is-warning', 'is-success')
     */
    confirmButtonClass: {
      type: String,
      default: 'is-danger'
    },
    /**
     * Enable countdown timer
     */
    showCountdown: {
      type: Boolean,
      default: false
    },
    /**
     * Countdown duration in seconds
     */
    countdownSeconds: {
      type: Number,
      default: 5
    },
    /**
     * Countdown message template (use {seconds} placeholder)
     */
    countdownMessageTemplate: {
      type: String,
      default: 'Vui lòng đợi {seconds} giây trước khi có thể xóa...'
    },
    /**
     * List of DataInput names that reference the item
     */
    dataInputNames: {
      type: Array,
      default: () => []
    },
    /**
     * List of Forwarder names that reference the item
     */
    forwarderNames: {
      type: Array,
      default: () => []
    },
    /**
     * Dependency warning message
     */
    dependencyWarningMessage: {
      type: String,
      default: 'Không xóa được do đang được cấu hình ở:'
    },
    /**
     * DataInput label for dependency section
     */
    dataInputLabel: {
      type: String,
      default: 'Nguồn dữ liệu'
    },
    /**
     * Forwarder label for dependency section
     */
    forwarderLabel: {
      type: String,
      default: 'Chuyển tiếp'
    }
  },
  data() {
    return {
      countdown: this.countdownSeconds,
      countdownActive: false,
      countdownInterval: null
    }
  },
  computed: {
    /**
     * Check if item has dependencies
     */
    hasDependencies() {
      return this.dataInputNames.length > 0 || this.forwarderNames.length > 0
    },
    /**
     * Formatted countdown message
     */
    countdownMessage() {
      return this.countdownMessageTemplate.replace('{seconds}', this.countdown)
    }
  },
  watch: {
    /**
     * Starts countdown when modal is shown (only if no dependencies and countdown is enabled)
     */
    show(newVal) {
      if (newVal && !this.hasDependencies && this.showCountdown) {
        this.startCountdown()
      } else {
        this.stopCountdown()
      }
    }
  },
  beforeDestroy() {
    this.stopCountdown()
  },
  methods: {
    /**
     * Starts the countdown timer
     */
    startCountdown() {
      this.countdown = this.countdownSeconds
      this.countdownActive = true
      
      this.countdownInterval = setInterval(() => {
        this.countdown--
        if (this.countdown <= 0) {
          this.stopCountdown()
        }
      }, 1000)
    },

    /**
     * Stops the countdown timer
     */
    stopCountdown() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
        this.countdownInterval = null
      }
      this.countdownActive = false
    },

    /**
     * Handles the confirmation action
     */
    handleConfirm() {
      if (!this.countdownActive) {
        this.$emit('confirm')
      }
    },

    /**
     * Handles the cancellation action
     */
    handleCancel() {
      this.stopCountdown()
      this.$emit('cancel')
    },

    /**
     * Handles modal close events (ESC key, click outside)
     * Prevent closing modal from outside, only allow via buttons
     */
    handleModalClose(value) {
      if (!value) {
        // Modal is being closed from outside, prevent it
        // Do nothing to keep modal open
      }
    }
  }
}
</script>

<style scoped>
.notification.is-warning {
  margin-top: 1rem;
}

.warning-message {
  margin-bottom: 1rem;
}

.warning-message .has-text-warning {
  color: #ff9800 !important;
  margin-bottom: 1rem;
}

.dependency-section {
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: #fff3cd;
  border-radius: 4px;
  border-left: 4px solid #ff9800;
}

.dependency-section strong {
  color: #856404;
  display: block;
  margin-bottom: 0.5rem;
}

.dependency-list {
  margin: 0;
  padding-left: 1.5rem;
  list-style-type: disc;
}

.dependency-item {
  margin-bottom: 0.25rem;
  color: #856404;
}

/* Style for warning text (red and bold) */
.has-text-grey {
  color: #f14668 !important;
  font-weight: 600 !important;
}
</style>

<style>
/* Hide the default close (X) button in the modal */
.modal-close {
  display: none !important;
}

/* Style for modal header title (bold and larger) */
.modal-card-head .modal-card-title,
.box [slot="header"] {
  font-weight: 700 !important;
  font-size: 1.25rem !important;
}
</style>
